import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { MAX_LOOKALIKE_DOMAINS } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import {
	ConfigFieldErrors,
	ConfigFieldKey,
	MAX_SEMANTIC_QUERY_LENGTH,
} from "../onboardingListValidation";

/** The limit each "too many/too long" message interpolates as `count`. */
const ERROR_LIMITS: Record<string, number> = {
	maxDomains: MAX_LOOKALIKE_DOMAINS,
	maxSemanticQuery: MAX_SEMANTIC_QUERY_LENGTH,
};

/** Two-column layout of a config form; fields opt into the full width themselves. */
export const ConfigGrid: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
	<div className="grid grid-cols-1 gap-x-4 tb:grid-cols-2">{children}</div>
);

export const ConfigField: FunctionComponent<{
	label: string;
	required?: boolean;
	error?: string;
	hint?: string;
	/** Spans both columns — for inputs that collect many values (domains, keywords). */
	full?: boolean;
	children: ReactNode;
}> = ({ label, required, error, hint, full, children }) => (
	<div className={classNames("mb-3 min-w-0", full && "tb:col-span-2")}>
		<div className="label-styles mb-1">
			{label}
			{required && <span className="ml-0.5 text-status-error">*</span>}
			{hint && <span className="ml-1 font-normal text-text-meta">{hint}</span>}
		</div>
		{children}
		{error && <div className="mt-1 text-xs text-status-error">{error}</div>}
	</div>
);

/** Resolves a field's error key to its message (max-domains carries a count). */
export const useFieldError = (errors: ConfigFieldErrors) => {
	const { t } = useTranslation();

	return (field: ConfigFieldKey): string | undefined => {
		const key = errors[field];

		return key
			? t(`onboarding.lists.config.errors.${key}`, { count: ERROR_LIMITS[key] })
			: undefined;
	};
};

/** Shorthand for the `onboarding.lists.config.*` strings every config form uses. */
export const useConfigTranslation = () => {
	const { t } = useTranslation();

	return (key: string) => t(`onboarding.lists.config.${key}`);
};
